/**
 * 学生卡片：家长首页列表项。
 * 用 Link 整卡可点（手机上好按），内部不再嵌套交互元素，避免点击目标冲突。
 *
 * ⚠️ 余额必须是 `StudentListItem` 里**必填**的那个字段，不能写成
 *   `student.pointsBalance ?? 0`：后端早期根本不返回这个字段，`?? 0`
 *   就把"没读到"渲染成了"0 积分"——孩子有 60 分，卡片上也是 0，且毫无报错。
 *   现在"没读到"是 `null`，界面显示「—」（看得见的缺失，而不是可信的错数）。
 */
import { Link } from 'react-router-dom';
import { gradeLabel, type SharePermission } from '@fht/shared';
import type { StudentListItem } from '../../lib/api/types';
import { Badge } from '../ui/Card';
import { cn } from '../../lib/utils';

const PERMISSION_LABEL: Record<SharePermission, string> = {
  owner: '我创建的',
  editor: '共享 · 可编辑',
  viewer: '共享 · 仅查看',
};

export function StudentCard({ student }: { student: StudentListItem }) {
  const isOwner = student.permission === 'owner';
  const avatar = student.avatarUrl
    ? null
    : student.gender === 'female'
      ? '👧'
      : student.gender === 'male'
        ? '👦'
        : '🧒';

  return (
    <Link
      to={`/students/${student.id}`}
      className={cn(
        'card flex items-center gap-4 transition-shadow hover:shadow-md',
        'focus-visible:ring-2 focus-visible:ring-brand-500',
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-3xl"
      >
        {avatar}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-base font-semibold text-slate-900">{student.nickname}</h3>
          <Badge tone={isOwner ? 'brand' : 'neutral'}>{PERMISSION_LABEL[student.permission]}</Badge>
        </div>
        <p className="mt-0.5 truncate text-sm text-slate-500">
          {gradeLabel(student.gradeCode)} · 用户名 {student.username}
        </p>
      </div>

      <div className="shrink-0 text-right">
        <p className="text-lg font-bold text-brand-700">
          {student.pointsBalance === null ? '—' : student.pointsBalance}
        </p>
        <p className="text-xs text-slate-400">积分</p>
      </div>

      <span aria-hidden="true" className="shrink-0 text-slate-300">
        ›
      </span>
    </Link>
  );
}
